import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Send } from 'lucide-react';
import Field from '../../components/admin/Field';
import ImageUploader from '../../components/admin/ImageUploader';
import RichEditor from '../../components/admin/RichEditor';
import { ErrorState, Loading } from '../../components/StatusStates';
import { useAdminPost, useCreatePost, useUpdatePost } from '../../hooks/use-admin';
import { ApiError } from '../../api/client';
import type { PostPayload } from '../../api/types';
import { tagsToText, textToTags, toSlug } from '../../lib/format';

interface FormState {
  title: string;
  slug: string;
  excerpt: string;
  tagsText: string;
  coverImage: string | null;
  status: 'draft' | 'published';
  publishedAtInput: string;
  content: string;
}

const EMPTY_FORM: FormState = {
  title: '',
  slug: '',
  excerpt: '',
  tagsText: '',
  coverImage: null,
  status: 'draft',
  publishedAtInput: '',
  content: '',
};

/** ISO 时间转 datetime-local 输入框所需的本地时间格式 */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export default function PostEditor() {
  const { id } = useParams<{ id: string }>();
  const postId = id ? Number(id) : null;
  const isEditing = postId !== null;

  const navigate = useNavigate();
  const postQuery = useAdminPost(isEditing ? postId : null);
  const createMutation = useCreatePost();
  // 新建模式下不会调用该 mutation，传入 -1 仅为保证 Hook 调用顺序稳定
  const updateMutation = useUpdatePost(postId ?? -1);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [baselineJson, setBaselineJson] = useState(() => JSON.stringify(EMPTY_FORM));

  // 编辑模式下回填数据
  useEffect(() => {
    if (postQuery.data) {
      const post = postQuery.data;
      const initialForm: FormState = {
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt ?? '',
        tagsText: tagsToText(post.tags),
        coverImage: post.coverImage,
        status: post.status,
        publishedAtInput: toLocalInput(post.publishedAt),
        content: post.content ?? '',
      };
      setForm(initialForm);
      setBaselineJson(JSON.stringify(initialForm));
      setSlugTouched(true);
    }
  }, [postQuery.data]);

  const dirty = JSON.stringify(form) !== baselineJson;

  // 未保存时离开页面的浏览器提示
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const update = (patch: Partial<FormState>) => {
    setSaved(false);
    setForm((current) => ({ ...current, ...patch }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.title.trim()) next.title = '标题不能为空';
    if (form.slug && !/^[A-Za-z0-9-]+$/.test(form.slug)) next.slug = '仅允许英文字母、数字与连字符';
    if (form.excerpt.length > 500) next.excerpt = '摘要最长 500 个字符';
    if (!form.content.trim()) next.content = '正文不能为空';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = (statusOverride?: 'draft' | 'published'): PostPayload => {
    const effectiveStatus = statusOverride ?? form.status;
    return {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      excerpt: form.excerpt.trim() || undefined,
      content: form.content,
      tags: textToTags(form.tagsText),
      coverImage: form.coverImage,
      status: effectiveStatus,
      publishedAt:
        effectiveStatus === 'published' && form.publishedAtInput
          ? new Date(form.publishedAtInput).toISOString()
          : null,
    };
  };

  const handleSubmit = async (statusOverride?: 'draft' | 'published') => {
    setFormError(null);
    if (!validate()) {
      setFormError('请先修正表单中标注的问题。');
      return;
    }
    try {
      if (isEditing) {
        await updateMutation.mutateAsync(buildPayload(statusOverride));
      } else {
        await createMutation.mutateAsync(buildPayload(statusOverride));
      }
      setSaved(true);
      navigate('/admin/posts');
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : '保存失败，请稍后重试');
    }
  };

  if (isEditing && postQuery.isLoading) return <Loading />;
  if (isEditing && (postQuery.isError || !postQuery.data)) {
    return <ErrorState message="文章加载失败" onRetry={() => postQuery.refetch()} />;
  }

  const submitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="editor-grid">
      <div className="toolbar" style={{ marginBottom: 0 }}>
        <Link to="/admin/posts" className="article-back" style={{ color: 'var(--color-ink-muted)' }}>
          <ArrowLeft size={15} aria-hidden="true" />
          返回列表
        </Link>
        <div className="toolbar-spacer" />
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => void handleSubmit('draft')}
          disabled={submitting}
        >
          {submitting ? <Loader2 size={14} className="spinner-sm" /> : <Save size={14} />}
          存为草稿
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => void handleSubmit('published')}
          disabled={submitting}
        >
          {submitting ? <Loader2 size={14} className="spinner-sm" /> : <Send size={14} />}
          {form.status === 'published' ? '保存更新' : '发布文章'}
        </button>
      </div>

      {formError && <div className="inline-alert inline-alert-error">{formError}</div>}
      {saved && <div className="inline-alert inline-alert-success">保存成功。</div>}

      <section className="panel-form">
        <div className="form-grid">
          <Field
            label="标题"
            required
            error={errors.title}
            className="form-field-full"
          >
            <input
              className={`form-input ${errors.title ? 'invalid' : ''}`}
              value={form.title}
              maxLength={200}
              placeholder="请输入文章标题"
              onChange={(event) => {
                const title = event.target.value;
                if (!slugTouched) update({ title, slug: toSlug(title) });
                else update({ title });
              }}
            />
          </Field>

          <Field
            label="链接标识（slug）"
            hint="用于文章网址，留空将自动生成；建议使用英文与连字符"
            error={errors.slug}
          >
            <input
              className={`form-input ${errors.slug ? 'invalid' : ''}`}
              value={form.slug}
              maxLength={220}
              placeholder="例如 engineering-notes"
              onChange={(event) => {
                setSlugTouched(true);
                update({ slug: event.target.value });
              }}
            />
          </Field>

          <Field label="状态">
            <select
              className="form-select"
              value={form.status}
              onChange={(event) =>
                update({ status: event.target.value as 'draft' | 'published' })
              }
            >
              <option value="draft">草稿</option>
              <option value="published">已发布</option>
            </select>
          </Field>

          <Field
            label="摘要"
            hint="将显示在文章列表中，不超过 500 字"
            error={errors.excerpt}
            className="form-field-full"
          >
            <textarea
              className={`form-textarea ${errors.excerpt ? 'invalid' : ''}`}
              value={form.excerpt}
              maxLength={500}
              rows={3}
              onChange={(event) => update({ excerpt: event.target.value })}
            />
          </Field>

          <Field label="标签" hint="多个标签以逗号或顿号分隔">
            <input
              className="form-input"
              value={form.tagsText}
              placeholder="工程实践, 系统设计"
              onChange={(event) => update({ tagsText: event.target.value })}
            />
          </Field>

          <Field label="发布时间" hint="留空则在发布时自动设为当前时间">
            <input
              type="datetime-local"
              className="form-input"
              value={form.publishedAtInput}
              onChange={(event) => update({ publishedAtInput: event.target.value })}
            />
          </Field>

          <Field label="封面图" className="form-field-full">
            <ImageUploader
              value={form.coverImage}
              onChange={(url) => update({ coverImage: url })}
            />
          </Field>
        </div>
      </section>

      <section className="panel-form">
        <Field label="正文（可视化编辑）" required error={errors.content}>
          <div style={{ marginTop: '0.5rem' }}>
            <RichEditor
              value={form.content}
              onChange={(content) => update({ content })}
            />
          </div>
        </Field>
      </section>
    </div>
  );
}
